import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSeo } from "@/lib/useSeo";
import RelatedReading from "@/components/RelatedReading";
import { Plus, Minus, ShoppingCart, ArrowLeft } from "@phosphor-icons/react";

function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200);
}

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [showBar, setShowBar] = useState(false);
  const addRowRef = useRef(null);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then(r => { setP(r.data); setActiveImg(0); });
  }, [id]);

  useEffect(() => {
    const el = addRowRef.current;
    if (!p || !el) return;
    const obs = new IntersectionObserver(([e]) => setShowBar(!e.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [p]);

  useSeo(p ? {
    title: `${p.title} — Stuffly`,
    description: stripHtml(p.description_html) || `Buy ${p.title} for $${p.price.toFixed(2)} NZD at Stuffly. Fast NZ dispatch and secure Stripe checkout.`,
    path: `/product/${p.id}`,
    image: p.image,
    type: "product",
    jsonLd: {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": p.title,
      "image": p.images || (p.image ? [p.image] : []),
      "description": stripHtml(p.description_html) || p.title,
      "sku": p.id,
      "brand": { "@type": "Brand", "name": p.vendor || "Stuffly" },
      "category": p.product_type || "General",
      "offers": {
        "@type": "Offer",
        "url": typeof window !== "undefined" ? window.location.href : "",
        "priceCurrency": "NZD",
        "price": p.price.toFixed(2),
        "availability": p.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition"
      }
    }
  } : { title: "Loading… — Stuffly", description: "Loading product details", path: "/" });

  if (!p) return <div className="p-12 text-center text-xs uppercase tracking-widest font-bold font-[Outfit]">Loading…</div>;

  const oos = p.in_stock === false;
  const discount = p.compare_at_price && p.compare_at_price > p.price
    ? Math.round((1 - p.price / p.compare_at_price) * 100) : null;

  return (
    <div>
      <div className="border-b border-zinc-900 px-6 md:px-12 py-4">
        <Link to="/products" className="text-xs uppercase tracking-widest font-bold font-[Outfit] hover:underline inline-flex items-center gap-2"><ArrowLeft size={14} weight="bold" /> Back to Catalogue</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 border-b border-zinc-900">
        <div className="border-r border-zinc-900">
          <div className="aspect-square bg-zinc-100 flex items-center justify-center overflow-hidden">
            {p.images?.[activeImg] ? (
              <img src={p.images[activeImg]} alt={p.title} className="w-full h-full object-contain p-12" data-testid="product-main-image" />
            ) : <div className="text-zinc-400">No Image</div>}
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-5 border-t border-zinc-900">
              {p.images.slice(0, 5).map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square border-r border-zinc-900 last:border-r-0 bg-zinc-100 p-2 ${i === activeImg ? 'ring-2 ring-inset ring-[#ff3333]' : ''}`}>
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-8 md:p-12 flex flex-col">
          <div className="text-xs uppercase tracking-widest font-bold font-[Outfit] text-zinc-500">
            <Link to={`/products?category=${encodeURIComponent(p.category || "")}`} className="hover:underline hover:text-[#ff3333]" data-testid="product-category-link">{p.category || "General"}</Link>
            {" · "}{p.vendor || "Unbranded"}
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter uppercase font-[Outfit] mt-3 leading-tight" data-testid="product-title">{p.title}</h1>
          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-4xl font-black font-[Outfit] tracking-tighter" data-testid="product-price">${p.price.toFixed(2)}</span>
            <span className="text-sm text-zinc-500">NZD</span>
            {p.compare_at_price && p.compare_at_price > p.price && (
              <span className="text-sm text-zinc-500 line-through ml-2">${p.compare_at_price.toFixed(2)}</span>
            )}
            {oos && <span className="tag bg-zinc-900 text-white border-zinc-900" data-testid="product-oos-badge">Out of Stock</span>}
            {!oos && discount && <span className="tag bg-[#ff3333] text-white border-[#ff3333]">-{discount}%</span>}
          </div>
          <div className="mt-2 text-xs uppercase tracking-widest text-zinc-500 font-[Outfit] font-bold">Free shipping over $100 · 1 Year Warranty</div>

          <div ref={addRowRef} className="flex items-center gap-4 mt-8">
            <div className={`flex border border-zinc-900 ${oos ? "opacity-40 pointer-events-none" : ""}`}>
              <button data-testid="qty-minus" onClick={() => setQty(q => Math.max(1, q - 1))} className="w-11 h-11 hover:bg-zinc-900 hover:text-white"><Minus size={14} weight="bold" className="m-auto" /></button>
              <span data-testid="qty-value" className="w-12 h-11 leading-[44px] text-center font-bold border-x border-zinc-900">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(q => q + 1)} className="w-11 h-11 hover:bg-zinc-900 hover:text-white"><Plus size={14} weight="bold" className="m-auto" /></button>
            </div>
            <button data-testid="add-to-cart-btn" onClick={() => add(p, qty)} disabled={oos} className={`btn-brutal flex-1 ${oos ? "opacity-40 pointer-events-none" : "btn-red"}`}>
              {oos ? "Out of Stock" : <><ShoppingCart size={16} weight="bold" className="mr-2" /> Add To Cart</>}
            </button>
          </div>
          {oos && <p className="mt-3 text-xs text-zinc-500" data-testid="oos-note">This item is currently unavailable from our supplier. Check back soon — stock updates twice daily.</p>}

          {p.description_html && (
            <div className="mt-10 border-t border-zinc-900 pt-6">
              <div className="text-xs uppercase tracking-widest font-bold font-[Outfit] mb-3">Description</div>
              <div className="prose prose-sm max-w-none text-sm leading-relaxed text-zinc-700" dangerouslySetInnerHTML={{ __html: p.description_html }} />
            </div>
          )}
        </div>
      </div>
      <RelatedReading productId={p.id} />

      {showBar && !oos && (
        <div className="sticky-add-bar md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-zinc-900 flex items-stretch" data-testid="sticky-add-bar">
          <button
            data-testid="sticky-add-to-cart-btn"
            onClick={() => add(p, qty)}
            className="flex-[3] bg-[#ff3333] text-white font-[Outfit] font-extrabold uppercase tracking-widest text-xs py-4 flex items-center justify-center gap-2 active:bg-[#cc0000]"
          >
            <ShoppingCart size={16} weight="bold" /> Add To Cart{qty > 1 ? ` ×${qty}` : ""}
          </button>
          <div className="flex-[2] flex items-center justify-center border-l border-zinc-900 px-2">
            <span className="font-black font-[Outfit] text-lg tracking-tighter" data-testid="sticky-bar-price">${(p.price * qty).toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
